const normalizeLoanValue = (value) => `${value || ""}`.trim().toLowerCase();
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseAmount = (value) => {
  const raw = `${value || ""}`.trim();
  if (!raw) return { kind: "empty", value: null };
  const parsed = Number(raw.replace(/[$,]/g, ""));
  if (!Number.isFinite(parsed)) return { kind: "nan", value: null };
  if (parsed <= 0) return { kind: "nonPositive", value: parsed };
  return { kind: "ok", value: parsed };
};

const toFourDigitYear = (year) => {
  const parsed = Number(year);
  if (!Number.isFinite(parsed)) return null;
  return parsed < 100 ? 2000 + parsed : parsed;
};

const formatDateParts = (year, month, day) => {
  const yyyy = toFourDigitYear(year);
  const mmNumber = Number(month);
  const ddNumber = Number(day);
  if (!yyyy || !Number.isFinite(mmNumber) || !Number.isFinite(ddNumber)) return null;
  const parsed = new Date(yyyy, mmNumber - 1, ddNumber);
  if (
    parsed.getFullYear() !== yyyy ||
    parsed.getMonth() + 1 !== mmNumber ||
    parsed.getDate() !== ddNumber
  ) {
    return null;
  }
  const mm = `${mmNumber}`.padStart(2, "0");
  const dd = `${ddNumber}`.padStart(2, "0");
  return `${mm}/${dd}/${yyyy}`;
};

const toDateString = (dateObj) =>
  formatDateParts(dateObj.getFullYear(), dateObj.getMonth() + 1, dateObj.getDate());

const parseExcelSerialDate = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  const asDate = new Date(Date.UTC(1899, 11, 30) + Math.floor(numeric) * MS_PER_DAY);
  return formatDateParts(asDate.getUTCFullYear(), asDate.getUTCMonth() + 1, asDate.getUTCDate());
};

const parseDateString = (value) => {
  const raw = `${value}`.trim();
  if (!raw) return null;

  if (/^\d+(\.\d+)?$/.test(raw)) {
    return parseExcelSerialDate(raw);
  }

  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    return formatDateParts(usMatch[3], usMatch[1], usMatch[2]);
  }

  return null;
};

const isFutureDateString = (mmddyyyy) => {
  const match = mmddyyyy.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return false;
  const parsed = new Date(Number(match[3]), Number(match[1]) - 1, Number(match[2]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed.getTime() > today.getTime();
};

const parseDateValue = (value) => {
  if (value === null || value === undefined || `${value}`.trim() === "") {
    return { kind: "empty", value: null };
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const formatted = toDateString(value);
    if (formatted && isFutureDateString(formatted)) return { kind: "future", value: formatted };
    return { kind: "ok", value: formatted };
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const parsed = parseExcelSerialDate(value);
    if (parsed) {
      if (isFutureDateString(parsed)) return { kind: "future", value: parsed };
      return { kind: "ok", value: parsed };
    }
  }
  const parsed = parseDateString(value);
  if (!parsed) {
    return { kind: "invalid", value: `${value}`.trim() };
  }
  if (isFutureDateString(parsed)) return { kind: "future", value: parsed };
  return { kind: "ok", value: parsed };
};

const stripLeadingZeros = (value) => `${value || ""}`.replace(/^0+/, "");

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const validateRows = (rows = [], propertyList = []) => {
  const loanIdMap = new Map();
  const availableFundsByLoan = new Map();
  const availableFundsByRecId = new Map();
  propertyList.forEach((property) => {
    const loanRecId = property?.loanRecId || property?.LoanRecid || property?.loanrecid || "";
    if (!normalizeLoanValue(loanRecId)) return;
    const account = property?.account || property?.Account || property?.loanNumber || property?.LoanNumber || "";
    const available =
      typeof property?.availableFundsValue === "number" && Number.isFinite(property.availableFundsValue)
        ? property.availableFundsValue
        : null;
    if (available !== null) availableFundsByRecId.set(loanRecId, available);
    [loanRecId, account].forEach((value) => {
      const key = normalizeLoanValue(value);
      if (!key) return;
      if (!loanIdMap.has(key)) loanIdMap.set(key, loanRecId);
      if (available !== null && !availableFundsByLoan.has(key)) availableFundsByLoan.set(key, available);
      const stripped = stripLeadingZeros(key);
      if (stripped && stripped !== key && !loanIdMap.has(stripped)) {
        loanIdMap.set(stripped, loanRecId);
        if (available !== null && !availableFundsByLoan.has(stripped)) availableFundsByLoan.set(stripped, available);
      }
    });
  });

  const validated = rows.map((row) => {
    const reasons = [];
    const fieldErrors = {};
    const loanNo = `${row?.loanNo || ""}`.trim();
    const drawAmount = `${row?.drawAmount || ""}`.trim();
    const dateCheck = parseDateValue(row?.drawDate);
    const amountCheck = parseAmount(drawAmount);

    let loanRecId = null;
    let availableFundsValue = null;
    if (!loanNo) {
      reasons.push("Loan number is required");
      fieldErrors.loanNo = "Loan number is required";
    } else {
      const key = normalizeLoanValue(loanNo);
      const strippedKey = stripLeadingZeros(key);
      const match = loanIdMap.get(key) || loanIdMap.get(strippedKey);
      if (!match) {
        reasons.push("Loan number not in your accounts");
        fieldErrors.loanNo = "Loan number not in your accounts";
      } else {
        loanRecId = match;
        const funds = availableFundsByLoan.get(key);
        availableFundsValue = funds !== undefined ? funds : availableFundsByLoan.get(strippedKey) ?? null;
      }
    }

    if (amountCheck.kind === "empty") {
      reasons.push("Draw amount is required");
      fieldErrors.drawAmount = "Draw amount is required";
    }
    if (amountCheck.kind === "nan") {
      reasons.push("Draw amount must be a number");
      fieldErrors.drawAmount = "Draw amount must be a number";
    }
    if (amountCheck.kind === "nonPositive") {
      reasons.push("Draw amount must be greater than 0");
      fieldErrors.drawAmount = "Draw amount must be greater than 0";
    }
    if (
      amountCheck.kind === "ok" &&
      typeof availableFundsValue === "number" &&
      Number.isFinite(availableFundsValue) &&
      amountCheck.value > availableFundsValue
    ) {
      const cap = moneyFormatter.format(availableFundsValue);
      reasons.push(`Draw amount exceeds Available Funds (${cap})`);
      fieldErrors.drawAmount = `Exceeds Available Funds (${cap})`;
    }

    if (dateCheck.kind === "empty") {
      reasons.push("Draw date is required");
      fieldErrors.drawDate = "Draw date is required";
    }
    if (dateCheck.kind === "invalid") {
      reasons.push("Draw date must be in MM/DD/YYYY format");
      fieldErrors.drawDate = "Draw date must be in MM/DD/YYYY format";
    }
    if (dateCheck.kind === "future") {
      reasons.push("Draw date cannot be in the future");
      fieldErrors.drawDate = "Draw date cannot be in the future";
    }

    return {
      ...row,
      loanNo,
      drawAmount,
      drawDate: dateCheck.kind === "ok" || dateCheck.kind === "future" ? dateCheck.value : `${row?.drawDate || ""}`.trim(),
      loanRecId,
      amountValue: amountCheck.kind === "ok" ? amountCheck.value : null,
      status: reasons.length === 0 ? "valid" : "invalid",
      reasons,
      fieldErrors,
    };
  });

  const totalsByLoanRecId = new Map();
  validated.forEach((row) => {
    if (!row.loanRecId || typeof row.amountValue !== "number") return;
    totalsByLoanRecId.set(
      row.loanRecId,
      (totalsByLoanRecId.get(row.loanRecId) || 0) + row.amountValue
    );
  });

  return validated.map((row) => {
    if (!row.loanRecId || typeof row.amountValue !== "number") return row;
    const cap = availableFundsByRecId.get(row.loanRecId);
    const total = totalsByLoanRecId.get(row.loanRecId);
    if (typeof cap !== "number" || typeof total !== "number") return row;
    if (total <= cap) return row;

    const capStr = moneyFormatter.format(cap);
    const totalStr = moneyFormatter.format(total);
    const reason = `Combined draws for this loan (${totalStr}) exceed Available Funds (${capStr})`;
    const nextReasons = row.reasons.includes(reason) ? row.reasons : [...row.reasons, reason];
    return {
      ...row,
      reasons: nextReasons,
      fieldErrors: {
        ...row.fieldErrors,
        drawAmount: row.fieldErrors.drawAmount || `Combined exceeds ${capStr}`,
      },
      status: "invalid",
    };
  });
};
